/**
 * TravelTime — trial-period evaluation tool (4-month trial, ~2026-10-08).
 *
 * Set point(s) by clicking the map OR by searching an address / postcode
 * (PDOK Locatieserver, BAG-authoritative, free, no token), pick a mode and
 * parameters, then Generate. Five analysis modes:
 *   - Isochrone:    single departure → dissolved reachability polygon.
 *   - H3 grid:      single departure → per-cell mean travel time heatmap.
 *   - Distance map: single departure → reachability polygon by distance budget (km).
 *   - Matrix:       one origin → many destinations, travel time per destination.
 *   - Route:        origin → one destination, multi-modal itinerary + geometry.
 * Every output can be downloaded as GeoJSON for QGIS.
 *
 * Calls api.studiobereikbaar.nl/oapi/processes/{traveltime-isochrone,traveltime-h3,
 * traveltime-distance-map,traveltime-matrix,traveltime-route}/execution.
 * The trial_token is bundled at build time from VITE_TRAVELTIME_TRIAL_TOKEN —
 * acceptable because this page is behind the forwardauth gate.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { BitmapLayer, GeoJsonLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { TileLayer, H3HexagonLayer } from '@deck.gl/geo-layers';
import type { Layer, PickingInfo } from '@deck.gl/core';

import { Sidebar } from '@/components/layout/Sidebar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { executeProcess } from '@/lib/sb-api-client';

const TRIAL_TOKEN = (import.meta.env.VITE_TRAVELTIME_TRIAL_TOKEN as string | undefined) ?? '';
const SB_API_BASE =
  (import.meta.env.VITE_SB_API_BASE as string | undefined) ??
  'https://api.studiobereikbaar.nl';

const PDOK_BASE = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1';

const TRIAL_EXPIRES = '2026-10-08';

const MODES = [
  { id: 'walking', label: 'Walking' },
  { id: 'cycling', label: 'Cycling' },
  { id: 'driving', label: 'Driving' },
  { id: 'public_transport', label: 'Public transport (walk both ends)' },
  { id: 'cycling+public_transport', label: 'Cycle to station + PT + walk' },
  { id: 'driving+train', label: 'Drive to station + train + walk' },
] as const;

type Mode = (typeof MODES)[number]['id'];

/** Modes that ride a published PT/train schedule — get the GTFS validity window. */
const PT_MODES = new Set<Mode>([
  'public_transport',
  'cycling+public_transport',
  'driving+train',
]);

type AnalysisMode = 'isochrone' | 'h3' | 'distance' | 'matrix' | 'route';

const ANALYSIS_MODES: ReadonlyArray<{ id: AnalysisMode; label: string }> = [
  { id: 'isochrone', label: 'Isochrone' },
  { id: 'h3', label: 'H3 grid' },
  { id: 'distance', label: 'Distance map' },
  { id: 'matrix', label: 'Matrix' },
  { id: 'route', label: 'Route' },
];

/** Process id per analysis mode (also used in the POST footer + filenames). */
const PROCESS_ID: Record<AnalysisMode, string> = {
  isochrone: 'traveltime-isochrone',
  h3: 'traveltime-h3',
  distance: 'traveltime-distance-map',
  matrix: 'traveltime-matrix',
  route: 'traveltime-route',
};

const H3_RESOLUTIONS = [9, 10, 11] as const;
type H3Resolution = (typeof H3_RESOLUTIONS)[number];

type LatLng = { lat: number; lng: number };

interface IsochroneResult {
  geometry: GeoJSON.Feature<GeoJSON.MultiPolygon>;
  meta: {
    hits_used: number;
    n_shapes: number;
    vendor: string;
    trial_expires: string;
    gtfs_valid_from?: string;
    gtfs_valid_to?: string;
  };
}

interface H3Cell {
  h3_index: string;
  mean_min: number;
}

/**
 * The traveltime-h3 OGC process returns two named outputs — the FeatureCollection
 * under `grid` and the metadata under `meta` — mirroring how traveltime-isochrone
 * returns `geometry` + `meta`. (Not a flat FeatureCollection.)
 */
interface H3Result {
  grid: GeoJSON.FeatureCollection<GeoJSON.Polygon, H3Cell>;
  meta: {
    hits_used: number;
    n_cells: number;
    vendor: string;
    trial_expires: string;
    gtfs_valid_from?: string;
    gtfs_valid_to?: string;
    /** Set when the requested travel_time exceeded the resolution cap and was clamped. */
    clamp_note?: string;
  };
}

interface DistanceMapResult {
  geometry: GeoJSON.Feature<GeoJSON.MultiPolygon>;
  meta: {
    hits_used: number;
    n_shapes: number;
    vendor: string;
    trial_expires: string;
    gtfs_valid_from?: string;
    gtfs_valid_to?: string;
    travel_distance_km: number;
  };
}

interface MatrixDestination {
  id: string;
  lat: number;
  lng: number;
}

interface MatrixResultRow {
  id: string;
  lat: number;
  lng: number;
  reachable: boolean;
  travel_time_min: number;
  distance_m: number;
}

interface MatrixResult {
  results: MatrixResultRow[];
  meta: {
    hits_used: number;
    n_destinations: number;
    n_reachable: number;
    vendor: string;
    trial_expires: string;
    gtfs_valid_from?: string;
    gtfs_valid_to?: string;
  };
}

interface RouteLegProps {
  part_index: number;
  mode: string;
  type: string;
  travel_time_min: number;
  distance_m: number;
  directions?: string;
}

interface RouteSummaryLeg {
  mode: string;
  travel_time_min: number;
  distance_m: number;
}

interface RouteResult {
  route: GeoJSON.FeatureCollection<GeoJSON.LineString, RouteLegProps>;
  summary: {
    total_travel_time_min: number;
    total_distance_m: number;
    departure_time: string;
    arrival_time: string;
    legs: RouteSummaryLeg[];
  };
  meta: {
    hits_used: number;
    vendor: string;
    trial_expires: string;
    gtfs_valid_from?: string;
    gtfs_valid_to?: string;
  };
}

interface PdokSuggestion {
  id: string;
  weergavenaam: string;
  type: string;
}

const POLY_FILL: [number, number, number, number] = [230, 46, 115, 80]; // SB eugenia, translucent
const POLY_STROKE: [number, number, number, number] = [122, 17, 38, 220];

/** Per-leg-mode colours for the Route line. Keys are the `properties.mode` strings. */
function routeLegColor(legMode: string): [number, number, number, number] {
  const m = legMode.toLowerCase();
  if (m.includes('walk')) return [120, 120, 120, 230]; // grey
  if (m.includes('cycl') || m.includes('bike')) return [46, 160, 90, 230]; // green
  if (m.includes('driv') || m.includes('car')) return [240, 170, 40, 230]; // amber
  // public_transport / train / pt
  return [230, 46, 115, 230]; // eugenia
}

/**
 * Sequential SB ramp for travel time. Lower minutes = closer = more
 * eugenia (230,46,115); higher minutes fade toward a pale grey. `t` is the
 * normalised position in [0,1] where 0 = fastest, 1 = slowest.
 */
function rampColor(t: number, alpha = 190): [number, number, number, number] {
  const c = Math.max(0, Math.min(1, t));
  // eugenia -> pale grey
  const r = Math.round(230 + (226 - 230) * c);
  const g = Math.round(46 + (231 - 46) * c);
  const b = Math.round(115 + (235 - 115) * c);
  return [r, g, b, alpha];
}

/** Parse PDOK `centroide_ll` WKT "POINT(lng lat)" (WGS84). */
function parseCentroideLL(wkt: string): { lat: number; lng: number } | null {
  const m = /POINT\(([-\d.]+)\s+([-\d.]+)\)/.exec(wkt);
  if (!m) return null;
  return { lng: parseFloat(m[1]), lat: parseFloat(m[2]) };
}

/**
 * Reproducible default for the departure picker: the next weekday at 08:00
 * local (skips Sat/Sun), formatted for an <input type="datetime-local">
 * (`YYYY-MM-DDTHH:mm`). Mirrors the backend's weekday-rush default so the
 * field is pre-populated rather than blank.
 */
/** Format a Date as an `<input type="datetime-local">` value (local wall-clock). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function defaultDepartureLocal(): string {
  const d = new Date();
  d.setHours(8, 0, 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return toLocalInput(d);
}

/**
 * Convert a `datetime-local` value (local wall-clock, no offset) to an
 * offset-aware ISO-8601 string in UTC (…Z). `new Date(local)` interprets the
 * input in the browser's timezone, so 09:00 in Amsterdam (CEST) becomes
 * 07:00Z — the correct instant for the vendor. Returns null when blank/invalid
 * so the caller falls back to the backend default.
 */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Render a GeoJSON MultiPolygon (+ optional departure marker) as a standalone,
 * design-tool-ready SVG string. Web-Mercator projection fitted to the geometry
 * bounds (north-up, undistorted). Holes are handled via fill-rule="evenodd".
 * SB eugenia fill + dark-eugenia stroke. No basemap — clean vector for
 * Figma / Illustrator.
 */
function isochroneToSvg(
  geometry: GeoJSON.Feature<GeoJSON.MultiPolygon>,
  center: LatLng | null,
  opts: { width?: number; margin?: number } = {},
): string {
  const width = opts.width ?? 1000;
  const margin = opts.margin ?? 24;

  const toMerc = (lng: number, lat: number): [number, number] => {
    const x = (lng + 180) / 360;
    const s = Math.sin((lat * Math.PI) / 180);
    const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
    return [x, y];
  };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const polygons = geometry.geometry.coordinates.map((poly) =>
    poly.map((ring) =>
      ring.map(([lng, lat]) => {
        const [mx, my] = toMerc(lng, lat);
        if (mx < minX) minX = mx;
        if (mx > maxX) maxX = mx;
        if (my < minY) minY = my;
        if (my > maxY) maxY = my;
        return [mx, my] as [number, number];
      }),
    ),
  );

  let cMerc: [number, number] | null = null;
  if (center) {
    cMerc = toMerc(center.lng, center.lat);
    minX = Math.min(minX, cMerc[0]);
    maxX = Math.max(maxX, cMerc[0]);
    minY = Math.min(minY, cMerc[1]);
    maxY = Math.max(maxY, cMerc[1]);
  }

  const spanX = maxX - minX || 1e-9;
  const spanY = maxY - minY || 1e-9;
  const scale = (width - 2 * margin) / spanX;
  const height = spanY * scale + 2 * margin;

  const fmt = (n: number) => n.toFixed(2);
  // Mercator y already increases southward, so screen-y needs no flip.
  const sx = (mx: number) => fmt(margin + (mx - minX) * scale);
  const sy = (my: number) => fmt(margin + (my - minY) * scale);

  const d = polygons
    .map((poly) =>
      poly
        .map((ring) => `M ${ring.map(([mx, my]) => `${sx(mx)} ${sy(my)}`).join(' L ')} Z`)
        .join(' '),
    )
    .join(' ');

  const marker = cMerc
    ? `\n  <circle cx="${sx(cMerc[0])}" cy="${sy(cMerc[1])}" r="5" fill="#000" stroke="#fff" stroke-width="2"/>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${fmt(width)}" height="${fmt(
    height,
  )}" viewBox="0 0 ${fmt(width)} ${fmt(height)}">
  <path d="${d}" fill="rgb(230,46,115)" fill-opacity="0.31" stroke="rgb(122,17,38)" stroke-width="2" fill-rule="evenodd"/>${marker}
</svg>`;
}

/** Where a resolved search/click point should land for the active mode. */
type PointTarget = 'origin' | 'destination';

export default function TravelTime() {
  const { user } = useAuth();

  // Single departure / origin point — shared by isochrone, h3, distance, and
  // serves as the origin for matrix + route.
  const [point, setPoint] = useState<LatLng | null>(null);
  const [mode, setMode] = useState<Mode>('walking');
  const [travelTimeMin, setTravelTimeMin] = useState(20);
  const [dissolveEnabled, setDissolveEnabled] = useState(false);
  const [dissolveMinutes, setDissolveMinutes] = useState(60);

  // Departure date & time (shared primary param). datetime-local wall-clock;
  // converted to UTC ISO at request time. Blank → backend default.
  const [departureTime, setDepartureTime] = useState<string>(defaultDepartureLocal);
  // Picker floor — no past departures (the vendor rejects them for PT modes).
  const minDeparture = useMemo(() => toLocalInput(new Date()), []);

  // Distance-map budget (km).
  const [travelDistanceKm, setTravelDistanceKm] = useState(5);

  // Matrix: many destinations + its own time budget.
  const [destinations, setDestinations] = useState<MatrixDestination[]>([]);
  const [matrixTimeMin, setMatrixTimeMin] = useState(45);

  // Route: single destination.
  const [routeDest, setRouteDest] = useState<LatLng | null>(null);

  // Which point the next search/click resolves to (matrix/route point picker).
  const [pointTarget, setPointTarget] = useState<PointTarget>('origin');

  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('isochrone');
  const [h3Resolution, setH3Resolution] = useState<H3Resolution>(10);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IsochroneResult | null>(null);
  const [h3Result, setH3Result] = useState<H3Result | null>(null);
  const [distanceResult, setDistanceResult] = useState<DistanceMapResult | null>(null);
  const [matrixResult, setMatrixResult] = useState<MatrixResult | null>(null);
  const [routeResult, setRouteResult] = useState<RouteResult | null>(null);

  // Address / postcode search (PDOK Locatieserver).
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PdokSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [viewState, setViewState] = useState({
    longitude: 4.9041,
    latitude: 52.0907,
    zoom: 7,
    pitch: 0,
    bearing: 0,
  });

  const usesSingleDeparture =
    analysisMode === 'isochrone' || analysisMode === 'h3' || analysisMode === 'distance';
  const usesDistance = analysisMode === 'distance';
  const usesMatrix = analysisMode === 'matrix';
  const usesRoute = analysisMode === 'route';
  // Matrix + route resolve a search/click to origin OR a second point.
  const usesPointPicker = usesMatrix || usesRoute;

  // --- PDOK autocomplete (debounced) ----------------------------------------
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const url = `${PDOK_BASE}/suggest?q=${encodeURIComponent(q)}&rows=8&fq=type:(adres OR postcode OR woonplaats OR weg)`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) return;
        const json = (await res.json()) as {
          response?: { docs?: PdokSuggestion[] };
        };
        setSuggestions(json.response?.docs ?? []);
        setShowSuggestions(true);
      } catch {
        /* aborted or network — ignore */
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  /** Drop a resolved WGS84 coordinate onto the right target for the active mode. */
  const applyResolvedCoord = useCallback(
    (coord: LatLng) => {
      if (usesMatrix && pointTarget === 'destination') {
        setDestinations((ds) => [
          ...ds,
          { id: `D${ds.length + 1}`, lat: coord.lat, lng: coord.lng },
        ]);
      } else if (usesRoute && pointTarget === 'destination') {
        setRouteDest(coord);
      } else {
        setPoint(coord);
      }
    },
    [usesMatrix, usesRoute, pointTarget],
  );

  const selectSuggestion = useCallback(
    async (s: PdokSuggestion) => {
      setQuery('');
      setShowSuggestions(false);
      setSuggestions([]);
      try {
        const url = `${PDOK_BASE}/lookup?id=${encodeURIComponent(s.id)}&fl=id,weergavenaam,centroide_ll`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`PDOK lookup ${res.status}`);
        const json = (await res.json()) as {
          response?: { docs?: Array<{ centroide_ll?: string }> };
        };
        const wkt = json.response?.docs?.[0]?.centroide_ll;
        if (!wkt) throw new Error('No centroid returned for that address.');
        const coord = parseCentroideLL(wkt);
        if (!coord) throw new Error('Could not parse PDOK centroid.');
        applyResolvedCoord(coord);
        setViewState((vs) => ({
          ...vs,
          longitude: coord.lng,
          latitude: coord.lat,
          zoom: Math.max(vs.zoom, 13),
          transitionDuration: 600,
        }));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [applyResolvedCoord],
  );

  const generate = useCallback(async () => {
    if (!TRIAL_TOKEN) {
      setError(
        'VITE_TRAVELTIME_TRIAL_TOKEN not configured in this build — contact Giovanni.',
      );
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const depIso = localToIso(departureTime);
      if (analysisMode === 'h3') {
        if (!point) throw new Error('Set a departure point first.');
        const inputs: Record<string, unknown> = {
          trial_token: TRIAL_TOKEN,
          lat: point.lat,
          lng: point.lng,
          mode,
          travel_time_min: travelTimeMin,
          resolution: h3Resolution,
        };
        if (depIso) inputs.departure_time = depIso;
        const res = await executeProcess<H3Result>('traveltime-h3', inputs);
        setH3Result(res);
        setResult(null);
        setDistanceResult(null);
        setMatrixResult(null);
        setRouteResult(null);
      } else if (analysisMode === 'distance') {
        if (!point) throw new Error('Set a departure point first.');
        const inputs: Record<string, unknown> = {
          trial_token: TRIAL_TOKEN,
          lat: point.lat,
          lng: point.lng,
          mode,
          travel_distance_km: travelDistanceKm,
        };
        if (depIso) inputs.departure_time = depIso;
        const res = await executeProcess<DistanceMapResult>(
          'traveltime-distance-map',
          inputs,
        );
        setDistanceResult(res);
        setResult(null);
        setH3Result(null);
        setMatrixResult(null);
        setRouteResult(null);
      } else if (analysisMode === 'matrix') {
        if (!point) throw new Error('Set an origin point first.');
        if (destinations.length === 0) {
          throw new Error('Add at least one destination (click the map or search).');
        }
        const inputs: Record<string, unknown> = {
          trial_token: TRIAL_TOKEN,
          origin_lat: point.lat,
          origin_lng: point.lng,
          destinations: destinations.map((d) => ({ id: d.id, lat: d.lat, lng: d.lng })),
          mode,
          travel_time_min: matrixTimeMin,
        };
        if (depIso) inputs.departure_time = depIso;
        const res = await executeProcess<MatrixResult>('traveltime-matrix', inputs);
        setMatrixResult(res);
        setResult(null);
        setH3Result(null);
        setDistanceResult(null);
        setRouteResult(null);
      } else if (analysisMode === 'route') {
        if (!point) throw new Error('Set an origin point first.');
        if (!routeDest) throw new Error('Set a destination point first.');
        const inputs: Record<string, unknown> = {
          trial_token: TRIAL_TOKEN,
          origin_lat: point.lat,
          origin_lng: point.lng,
          dest_lat: routeDest.lat,
          dest_lng: routeDest.lng,
          mode,
        };
        if (depIso) inputs.departure_time = depIso;
        const res = await executeProcess<RouteResult>('traveltime-route', inputs);
        setRouteResult(res);
        setResult(null);
        setH3Result(null);
        setDistanceResult(null);
        setMatrixResult(null);
      } else {
        if (!point) throw new Error('Set a departure point first.');
        const inputs: Record<string, unknown> = {
          trial_token: TRIAL_TOKEN,
          lat: point.lat,
          lng: point.lng,
          mode,
          travel_time_min: travelTimeMin,
        };
        if (dissolveEnabled) {
          inputs.pt_dissolve_minutes = dissolveMinutes;
        }
        if (depIso) inputs.departure_time = depIso;
        const res = await executeProcess<IsochroneResult>('traveltime-isochrone', inputs);
        setResult(res);
        setH3Result(null);
        setDistanceResult(null);
        setMatrixResult(null);
        setRouteResult(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [
    point,
    mode,
    travelTimeMin,
    dissolveEnabled,
    dissolveMinutes,
    departureTime,
    analysisMode,
    h3Resolution,
    travelDistanceKm,
    destinations,
    matrixTimeMin,
    routeDest,
  ]);

  const downloadGeoJSON = useCallback(() => {
    let fc: GeoJSON.FeatureCollection | null = null;
    let suffix = '';
    if (analysisMode === 'h3' && h3Result) {
      fc = h3Result.grid;
      suffix = `h3r${h3Resolution}`;
    } else if (analysisMode === 'distance' && distanceResult) {
      fc = { type: 'FeatureCollection', features: [distanceResult.geometry] };
      suffix = `distance_${travelDistanceKm}km`;
    } else if (analysisMode === 'matrix' && matrixResult) {
      fc = {
        type: 'FeatureCollection',
        features: matrixResult.results.map((r) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [r.lng, r.lat] },
          properties: {
            id: r.id,
            reachable: r.reachable,
            travel_time_min: r.travel_time_min,
            distance_m: r.distance_m,
          },
        })),
      };
      suffix = `matrix_${matrixTimeMin}min`;
    } else if (analysisMode === 'route' && routeResult) {
      fc = routeResult.route;
      suffix = 'route';
    } else if (analysisMode === 'isochrone' && result) {
      fc = { type: 'FeatureCollection', features: [result.geometry] };
      suffix = `isochrone_${travelTimeMin}min`;
    }
    if (!fc) return;
    const blob = new Blob([JSON.stringify(fc, null, 2)], {
      type: 'application/geo+json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const anchor = point ?? routeDest;
    a.download = `traveltime_${suffix}_${mode}${
      anchor ? `_${anchor.lat.toFixed(4)}_${anchor.lng.toFixed(4)}` : ''
    }.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    analysisMode,
    result,
    h3Result,
    distanceResult,
    matrixResult,
    routeResult,
    h3Resolution,
    travelDistanceKm,
    matrixTimeMin,
    travelTimeMin,
    mode,
    point,
    routeDest,
  ]);

  /** Export the polygon output (isochrone / distance map) as a styled SVG. */
  const downloadSVG = useCallback(() => {
    let geom: GeoJSON.Feature<GeoJSON.MultiPolygon> | null = null;
    let suffix = '';
    if (analysisMode === 'isochrone' && result) {
      geom = result.geometry;
      suffix = `isochrone_${travelTimeMin}min`;
    } else if (analysisMode === 'distance' && distanceResult) {
      geom = distanceResult.geometry;
      suffix = `distance_${travelDistanceKm}km`;
    }
    if (!geom) return;
    const svg = isochroneToSvg(geom, point, { width: 1000 });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traveltime_${suffix}_${mode}${
      point ? `_${point.lat.toFixed(4)}_${point.lng.toFixed(4)}` : ''
    }.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [analysisMode, result, distanceResult, travelTimeMin, travelDistanceKm, mode, point]);

  const onMapClick = useCallback(
    (info: PickingInfo) => {
      if (!info.coordinate) return;
      const [lng, lat] = info.coordinate as [number, number];
      applyResolvedCoord({ lat, lng });
    },
    [applyResolvedCoord],
  );

  const removeDestination = useCallback((id: string) => {
    setDestinations((ds) => ds.filter((d) => d.id !== id));
  }, []);

  // H3 colour-scale domain (min/max mean_min across returned cells).
  const h3Domain = useMemo<[number, number] | null>(() => {
    if (!h3Result || h3Result.grid.features.length === 0) return null;
    let lo = Infinity;
    let hi = -Infinity;
    for (const f of h3Result.grid.features) {
      const v = f.properties.mean_min;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    return [lo, hi];
  }, [h3Result]);

  // Matrix colour-scale domain (min/max travel_time_min across reachable rows).
  const matrixDomain = useMemo<[number, number] | null>(() => {
    if (!matrixResult) return null;
    let lo = Infinity;
    let hi = -Infinity;
    for (const r of matrixResult.results) {
      if (!r.reachable) continue;
      if (r.travel_time_min < lo) lo = r.travel_time_min;
      if (r.travel_time_min > hi) hi = r.travel_time_min;
    }
    if (lo === Infinity) return null;
    return [lo, hi];
  }, [matrixResult]);

  const layers: Layer[] = useMemo(() => {
    const ls: Layer[] = [
      new TileLayer({
        // PDOK BRT achtergrondkaart (grijs) — keyless, EPSG:3857, 256 px, z0-19.
        // Was CARTO light_all: CARTO switched on API-key enforcement for its
        // raster (PNG) basemaps, and unkeyed tiles come back HTTP 200 with
        // "API KEY REQUIRED" baked into the image — monitoring stays green while
        // the map is visibly broken. Same swap as sb-nrm-toolkit / Spinnen.
        id: 'basemap-brt',
        data: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png',
        minZoom: 0,
        maxZoom: 19,
        tileSize: 256,
        renderSubLayers: (props) => {
          const { boundingBox } = props.tile;
          return new BitmapLayer(props, {
            data: undefined,
            image: props.data,
            bounds: [
              boundingBox[0][0],
              boundingBox[0][1],
              boundingBox[1][0],
              boundingBox[1][1],
            ],
          });
        },
      }),
    ];

    if (analysisMode === 'isochrone' && result) {
      ls.push(
        new GeoJsonLayer({
          id: 'isochrone',
          data: result.geometry,
          filled: true,
          stroked: true,
          getFillColor: POLY_FILL,
          getLineColor: POLY_STROKE,
          getLineWidth: 2,
          lineWidthMinPixels: 1.5,
          pickable: false,
        }),
      );
    }

    if (analysisMode === 'distance' && distanceResult) {
      ls.push(
        new GeoJsonLayer({
          id: 'distance-map',
          data: distanceResult.geometry,
          filled: true,
          stroked: true,
          getFillColor: POLY_FILL,
          getLineColor: POLY_STROKE,
          getLineWidth: 2,
          lineWidthMinPixels: 1.5,
          pickable: false,
        }),
      );
    }

    if (analysisMode === 'h3' && h3Result && h3Domain) {
      const [lo, hi] = h3Domain;
      const span = hi - lo || 1;
      ls.push(
        new H3HexagonLayer<GeoJSON.Feature<GeoJSON.Polygon, H3Cell>>({
          id: 'h3-grid',
          data: h3Result.grid.features,
          getHexagon: (f) => f.properties.h3_index,
          extruded: false,
          stroked: true,
          filled: true,
          getFillColor: (f) => rampColor((f.properties.mean_min - lo) / span),
          getLineColor: [255, 255, 255, 120],
          lineWidthMinPixels: 0.5,
          pickable: true,
          updateTriggers: {
            getFillColor: [lo, hi],
          },
        }),
      );
    }

    if (analysisMode === 'route' && routeResult) {
      ls.push(
        new GeoJsonLayer<RouteLegProps>({
          id: 'route',
          data: routeResult.route,
          stroked: true,
          filled: false,
          getLineColor: (f) => routeLegColor(f.properties.mode),
          getLineWidth: 4,
          lineWidthUnits: 'pixels',
          lineWidthMinPixels: 3,
          pickable: true,
          updateTriggers: {
            getLineColor: [routeResult],
          },
        }),
      );
    }

    if (analysisMode === 'matrix' && matrixResult) {
      const [lo, hi] = matrixDomain ?? [0, 1];
      const span = hi - lo || 1;
      ls.push(
        new ScatterplotLayer<MatrixResultRow>({
          id: 'matrix-destinations',
          data: matrixResult.results,
          getPosition: (d) => [d.lng, d.lat],
          getRadius: 7,
          radiusUnits: 'pixels',
          radiusMinPixels: 6,
          stroked: true,
          filled: true,
          lineWidthUnits: 'pixels',
          getLineWidth: 2,
          getFillColor: (d) =>
            d.reachable
              ? rampColor((d.travel_time_min - lo) / span, 230)
              : [0, 0, 0, 0], // unreachable = hollow
          getLineColor: (d) =>
            d.reachable ? [122, 17, 38, 220] : [150, 150, 150, 220],
          pickable: true,
          updateTriggers: {
            getFillColor: [lo, hi],
            getLineColor: [lo, hi],
          },
        }),
      );
      ls.push(
        new TextLayer<MatrixResultRow>({
          id: 'matrix-labels',
          data: matrixResult.results,
          getPosition: (d) => [d.lng, d.lat],
          getText: (d) => d.id,
          getSize: 11,
          getColor: [33, 33, 33, 255],
          getPixelOffset: [0, -14],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'bottom',
          fontWeight: 700,
        }),
      );
    } else if (usesMatrix && destinations.length > 0) {
      // Pre-generation: show the destinations the user has dropped.
      ls.push(
        new ScatterplotLayer<MatrixDestination>({
          id: 'matrix-pending-destinations',
          data: destinations,
          getPosition: (d) => [d.lng, d.lat],
          getRadius: 6,
          radiusUnits: 'pixels',
          radiusMinPixels: 5,
          stroked: true,
          filled: true,
          lineWidthUnits: 'pixels',
          getLineWidth: 2,
          getFillColor: [230, 46, 115, 200],
          getLineColor: [255, 255, 255, 255],
          pickable: false,
        }),
      );
      ls.push(
        new TextLayer<MatrixDestination>({
          id: 'matrix-pending-labels',
          data: destinations,
          getPosition: (d) => [d.lng, d.lat],
          getText: (d) => d.id,
          getSize: 11,
          getColor: [33, 33, 33, 255],
          getPixelOffset: [0, -14],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'bottom',
          fontWeight: 700,
        }),
      );
    }

    // Route destination marker (origin handled by the shared `point` layer below).
    if (usesRoute && routeDest) {
      ls.push(
        new ScatterplotLayer({
          id: 'route-destination',
          data: [routeDest],
          getPosition: (d: LatLng) => [d.lng, d.lat],
          getRadius: 6,
          radiusUnits: 'pixels',
          radiusMinPixels: 5,
          getFillColor: [230, 46, 115, 255],
          getLineColor: [255, 255, 255, 255],
          lineWidthUnits: 'pixels',
          getLineWidth: 2,
          stroked: true,
        }),
      );
    }

    if (point) {
      ls.push(
        new ScatterplotLayer({
          id: 'departure',
          data: [point],
          getPosition: (d: LatLng) => [d.lng, d.lat],
          getRadius: 6,
          radiusUnits: 'pixels',
          radiusMinPixels: 5,
          getFillColor: [0, 0, 0, 255],
          getLineColor: [255, 255, 255, 255],
          lineWidthUnits: 'pixels',
          getLineWidth: 2,
          stroked: true,
        }),
      );
    }
    return ls;
  }, [
    analysisMode,
    result,
    h3Result,
    h3Domain,
    distanceResult,
    matrixResult,
    matrixDomain,
    routeResult,
    destinations,
    usesMatrix,
    usesRoute,
    routeDest,
    point,
  ]);

  const meta =
    analysisMode === 'h3'
      ? h3Result?.meta
      : analysisMode === 'distance'
        ? distanceResult?.meta
        : analysisMode === 'matrix'
          ? matrixResult?.meta
          : analysisMode === 'route'
            ? routeResult?.meta
            : result?.meta;

  const hasOutput =
    analysisMode === 'h3'
      ? !!h3Result
      : analysisMode === 'distance'
        ? !!distanceResult
        : analysisMode === 'matrix'
          ? !!matrixResult
          : analysisMode === 'route'
            ? !!routeResult
            : !!result;

  const showGtfsWindow =
    PT_MODES.has(mode) && !!meta?.gtfs_valid_from && !!meta?.gtfs_valid_to;

  const getTooltip = useCallback(
    (info: PickingInfo) => {
      if (!info.object) return null;
      if (analysisMode === 'h3') {
        const f = info.object as GeoJSON.Feature<GeoJSON.Polygon, H3Cell>;
        return { text: `${f.properties.mean_min.toFixed(1)} min` };
      }
      if (analysisMode === 'matrix') {
        const r = info.object as MatrixResultRow;
        return {
          text: r.reachable
            ? `${r.id}: ${r.travel_time_min.toFixed(1)} min · ${(r.distance_m / 1000).toFixed(1)} km`
            : `${r.id}: unreachable`,
        };
      }
      if (analysisMode === 'route') {
        const f = info.object as GeoJSON.Feature<GeoJSON.LineString, RouteLegProps>;
        return {
          text: `${f.properties.mode}: ${f.properties.travel_time_min.toFixed(1)} min · ${(f.properties.distance_m / 1000).toFixed(1)} km`,
        };
      }
      return null;
    },
    [analysisMode],
  );

  // Can the active mode run? (Generate button gating.)
  const canGenerate = usesSingleDeparture
    ? !!point
    : usesMatrix
      ? !!point && destinations.length > 0
      : usesRoute
        ? !!point && !!routeDest
        : false;

  const headerLine = usesPointPicker
    ? 'Set origin + destination(s) by searching or clicking the map, then Generate.'
    : 'Search an address or click the map to set a departure point, then Generate.';

  const generateLabel = loading
    ? 'Generating…'
    : analysisMode === 'h3'
      ? 'Generate H3 grid'
      : analysisMode === 'distance'
        ? 'Generate distance map'
        : analysisMode === 'matrix'
          ? 'Generate matrix'
          : analysisMode === 'route'
            ? 'Generate route'
            : 'Generate isochrone';

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4F7FC]">
      <Sidebar activeItem="dashboard" user={user} />

      <main id="main-content" className="flex-1 overflow-hidden">
        <div className="flex h-full">
          {/* Map */}
          <div className="relative flex-1">
            <DeckGL
              viewState={viewState}
              onViewStateChange={(e) =>
                setViewState(e.viewState as typeof viewState)
              }
              controller={true}
              layers={layers}
              onClick={onMapClick}
              getTooltip={getTooltip}
              style={{ position: 'absolute', inset: '0' }}
            />
            {loading && (
              <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                <div className="flex items-center gap-2 rounded-lg bg-white/95 px-4 py-2 text-sm font-semibold text-sb-black shadow-lg">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[rgb(230,46,115)]" />
                  Generating…
                </div>
              </div>
            )}
            <div className="pointer-events-none absolute left-4 top-4 max-w-md rounded-lg bg-white/90 px-4 py-2 text-sm shadow">
              <p className="font-semibold text-sb-black">TravelTime</p>
              <p className="text-xs text-sb-grey">{headerLine}</p>
            </div>
            {point && (
              <div className="pointer-events-none absolute right-4 top-4 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-mono shadow">
                {usesPointPicker ? 'origin ' : ''}
                {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
              </div>
            )}

            {/* H3 legend */}
            {analysisMode === 'h3' && h3Result && h3Domain && (
              <div className="absolute bottom-4 left-4 rounded-lg bg-white/95 px-3 py-2 text-xs shadow">
                <p className="mb-1 font-semibold text-sb-black">Mean travel time</p>
                <div
                  className="h-2.5 w-44 rounded"
                  style={{
                    background:
                      'linear-gradient(to right, rgb(230,46,115), rgb(226,231,235))',
                  }}
                />
                <div className="mt-1 flex w-44 justify-between text-[10px] text-sb-grey">
                  <span>{h3Domain[0].toFixed(0)} min</span>
                  <span>{h3Domain[1].toFixed(0)} min</span>
                </div>
                <p className="mt-1 text-[10px] text-sb-grey">
                  {h3Result.meta.n_cells} cells · res {h3Resolution}
                </p>
              </div>
            )}

            {/* Matrix legend */}
            {analysisMode === 'matrix' && matrixResult && matrixDomain && (
              <div className="absolute bottom-4 left-4 rounded-lg bg-white/95 px-3 py-2 text-xs shadow">
                <p className="mb-1 font-semibold text-sb-black">Travel time to destination</p>
                <div
                  className="h-2.5 w-44 rounded"
                  style={{
                    background:
                      'linear-gradient(to right, rgb(230,46,115), rgb(226,231,235))',
                  }}
                />
                <div className="mt-1 flex w-44 justify-between text-[10px] text-sb-grey">
                  <span>{matrixDomain[0].toFixed(0)} min</span>
                  <span>{matrixDomain[1].toFixed(0)} min</span>
                </div>
                <p className="mt-1 text-[10px] text-sb-grey">
                  Hollow markers = unreachable within {matrixTimeMin} min
                </p>
              </div>
            )}

            {/* Route leg-colour legend */}
            {analysisMode === 'route' && routeResult && (
              <div className="absolute bottom-4 left-4 rounded-lg bg-white/95 px-3 py-2 text-xs shadow">
                <p className="mb-1 font-semibold text-sb-black">Route legs</p>
                <ul className="space-y-0.5">
                  {[
                    { label: 'Walk', c: 'rgb(120,120,120)' },
                    { label: 'Cycle', c: 'rgb(46,160,90)' },
                    { label: 'Drive', c: 'rgb(240,170,40)' },
                    { label: 'PT / train', c: 'rgb(230,46,115)' },
                  ].map((x) => (
                    <li key={x.label} className="flex items-center gap-2 text-[10px] text-sb-grey">
                      <span
                        className="inline-block h-1.5 w-5 rounded"
                        style={{ background: x.c }}
                      />
                      {x.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right panel */}
          <aside className="w-[360px] overflow-y-auto border-l border-gray-200 bg-white p-6">
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-semibold">4-month trial — expires {TRIAL_EXPIRES}</p>
              <p>No monthly usage cap; rate-limited per minute (~60 req/min).</p>
            </div>

            {/* Analysis-mode selector — two-row segmented control */}
            <div className="mb-4">
              <span className="mb-1 block text-xs font-semibold text-sb-grey">
                Analysis
              </span>
              <div className="flex flex-wrap gap-1 rounded-md border border-gray-300 p-1">
                {ANALYSIS_MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`min-w-[88px] flex-1 whitespace-nowrap rounded px-2 py-1.5 text-sm font-semibold transition ${
                      analysisMode === m.id
                        ? 'bg-[rgb(230,46,115)] text-white'
                        : 'text-sb-grey hover:bg-gray-100'
                    }`}
                    onClick={() => setAnalysisMode(m.id)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Address / postcode search */}
            <label className="mb-2 block">
              <span className="mb-1 block text-xs font-semibold text-sb-grey">
                {usesPointPicker
                  ? pointTarget === 'origin'
                    ? 'Search → origin'
                    : usesRoute
                      ? 'Search → destination'
                      : 'Search → add destination'
                  : 'Address or postcode'}
              </span>
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  placeholder="e.g. Coolsingel 40 Rotterdam"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg">
                    {suggestions.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left hover:bg-gray-100"
                          onClick={() => selectSuggestion(s)}
                        >
                          {s.weergavenaam}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <span className="mt-1 block text-[10px] text-sb-grey">
                Geocoded via PDOK Locatieserver (BAG).
              </span>
            </label>

            {/* Point-target picker (matrix + route) */}
            {usesPointPicker && (
              <div className="mb-4">
                <span className="mb-1 block text-xs font-semibold text-sb-grey">
                  Map click / search sets
                </span>
                <div className="flex rounded-md border border-gray-300 p-0.5">
                  <button
                    type="button"
                    className={`flex-1 rounded px-3 py-1.5 text-sm font-semibold transition ${
                      pointTarget === 'origin'
                        ? 'bg-sb-black text-white'
                        : 'text-sb-grey hover:bg-gray-100'
                    }`}
                    onClick={() => setPointTarget('origin')}
                  >
                    Origin
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded px-3 py-1.5 text-sm font-semibold transition ${
                      pointTarget === 'destination'
                        ? 'bg-[rgb(230,46,115)] text-white'
                        : 'text-sb-grey hover:bg-gray-100'
                    }`}
                    onClick={() => setPointTarget('destination')}
                  >
                    {usesRoute ? 'Destination' : 'Add destination'}
                  </button>
                </div>
              </div>
            )}

            <h2 className="mb-4 text-lg font-semibold text-sb-black">Parameters</h2>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-semibold text-sb-grey">Mode</span>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                {MODES.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>

            {/* Departure date & time — shared primary param */}
            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-semibold text-sb-grey">
                Departure date &amp; time
              </span>
              <input
                type="datetime-local"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={departureTime}
                min={minDeparture}
                onChange={(e) => setDepartureTime(e.target.value)}
              />
              <span className="mt-1 block text-[10px] text-sb-grey">
                Local time. Blank = next weekday 08:00. Matters most for PT modes
                (schedule-dependent).
              </span>
            </label>

            {/* Travel-time budget — isochrone, h3, matrix */}
            {(analysisMode === 'isochrone' ||
              analysisMode === 'h3' ||
              analysisMode === 'matrix') && (
              <label className="mb-3 block">
                <span className="mb-1 flex items-center justify-between text-xs font-semibold text-sb-grey">
                  {analysisMode === 'matrix'
                    ? 'Max travel-time budget'
                    : 'Travel time budget'}
                  <span className="text-sb-black">
                    {analysisMode === 'matrix' ? matrixTimeMin : travelTimeMin} min
                  </span>
                </span>
                <input
                  type="range"
                  min={5}
                  max={120}
                  step={5}
                  value={analysisMode === 'matrix' ? matrixTimeMin : travelTimeMin}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (analysisMode === 'matrix') setMatrixTimeMin(v);
                    else setTravelTimeMin(v);
                  }}
                  className="w-full"
                />
              </label>
            )}

            {/* Distance budget — distance-map mode */}
            {usesDistance && (
              <label className="mb-3 block">
                <span className="mb-1 flex items-center justify-between text-xs font-semibold text-sb-grey">
                  Distance budget
                  <span className="text-sb-black">{travelDistanceKm} km</span>
                </span>
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={travelDistanceKm}
                  onChange={(e) => setTravelDistanceKm(parseInt(e.target.value, 10))}
                  className="w-full"
                />
              </label>
            )}

            {/* H3 resolution (H3 mode only) */}
            {analysisMode === 'h3' && (
              <label className="mb-4 block">
                <span className="mb-1 block text-xs font-semibold text-sb-grey">
                  H3 resolution
                </span>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={h3Resolution}
                  onChange={(e) =>
                    setH3Resolution(parseInt(e.target.value, 10) as H3Resolution)
                  }
                >
                  {H3_RESOLUTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r} {r === 9 ? '(coarse ~0.1 km²)' : r === 10 ? '(default ~15k m²)' : '(fine ~2k m²)'}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Matrix: destination list */}
            {usesMatrix && (
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-sb-black">
                    Destinations ({destinations.length})
                  </span>
                  {destinations.length > 0 && (
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-sb-grey hover:text-red-700"
                      onClick={() => setDestinations([])}
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <p className="mt-1 text-[10px] text-sb-grey">
                  Switch the picker to “Add destination”, then click the map or search.
                  The whole matrix costs just 1 hit.
                </p>
                {destinations.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {destinations.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs"
                      >
                        <span className="font-mono">
                          <span className="font-semibold text-sb-black">{d.id}</span>{' '}
                          <span className="text-sb-grey">
                            {d.lat.toFixed(4)}, {d.lng.toFixed(4)}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="ml-2 text-sb-grey hover:text-red-700"
                          onClick={() => removeDestination(d.id)}
                          aria-label={`Remove ${d.id}`}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Route: destination summary */}
            {usesRoute && (
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                <span className="text-xs font-semibold text-sb-black">Destination</span>
                <p className="mt-1 text-xs font-mono text-sb-grey">
                  {routeDest
                    ? `${routeDest.lat.toFixed(4)}, ${routeDest.lng.toFixed(4)}`
                    : 'not set — switch picker to “Destination”'}
                </p>
                {routeDest && (
                  <button
                    type="button"
                    className="mt-1 text-[10px] font-semibold text-sb-grey hover:text-red-700"
                    onClick={() => setRouteDest(null)}
                  >
                    Clear destination
                  </button>
                )}
              </div>
            )}

            {/* PT-frequency dissolve (isochrone mode only) */}
            {analysisMode === 'isochrone' && (
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={dissolveEnabled}
                    onChange={(e) => setDissolveEnabled(e.target.checked)}
                  />
                  <span className="font-semibold text-sb-black">PT-frequency dissolve</span>
                </label>
                <p className="mt-1 pl-6 text-xs text-sb-grey">
                  Fires 4 staggered departures across the window and unions them server-side.
                  Recommended for any PT mode (otherwise you're snapshotting one schedule).
                  Uses 4 hits instead of 1.
                </p>
                {dissolveEnabled && (
                  <label className="mt-2 block pl-6">
                    <span className="mb-1 flex items-center justify-between text-xs text-sb-grey">
                      Window
                      <span className="text-sb-black">{dissolveMinutes} min</span>
                    </span>
                    <input
                      type="range"
                      min={30}
                      max={120}
                      step={15}
                      value={dissolveMinutes}
                      onChange={(e) => setDissolveMinutes(parseInt(e.target.value, 10))}
                      className="w-full"
                    />
                  </label>
                )}
              </div>
            )}

            <Button
              onClick={generate}
              disabled={loading || !canGenerate}
              className="mb-2 w-full"
            >
              {canGenerate || loading
                ? generateLabel
                : usesMatrix
                  ? 'Set origin + a destination'
                  : usesRoute
                    ? 'Set origin + destination'
                    : 'Set a departure point'}
            </Button>

            <Button
              onClick={downloadGeoJSON}
              disabled={!hasOutput}
              variant="outline"
              className="w-full"
            >
              Download GeoJSON
            </Button>

            <Button
              onClick={downloadSVG}
              disabled={
                !(
                  (analysisMode === 'isochrone' && !!result) ||
                  (analysisMode === 'distance' && !!distanceResult)
                )
              }
              variant="outline"
              className="mt-2 w-full"
            >
              Download SVG
            </Button>
            {(analysisMode === 'isochrone' || analysisMode === 'distance') && (
              <p className="mt-1 text-[10px] text-sb-grey">
                SVG = clean vector (shape + departure marker) for Figma / Illustrator.
              </p>
            )}

            {error && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
                {error}
              </div>
            )}

            {analysisMode === 'h3' && h3Result?.meta.clamp_note && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {h3Result.meta.clamp_note}
              </div>
            )}

            {/* Matrix results table */}
            {analysisMode === 'matrix' && matrixResult && (
              <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-sb-grey">
                    <tr>
                      <th className="px-2 py-1 text-left font-semibold">Dest</th>
                      <th className="px-2 py-1 text-right font-semibold">min</th>
                      <th className="px-2 py-1 text-right font-semibold">km</th>
                      <th className="px-2 py-1 text-center font-semibold">✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixResult.results.map((r) => (
                      <tr key={r.id} className="border-t border-gray-100">
                        <td className="px-2 py-1 font-mono font-semibold text-sb-black">
                          {r.id}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {r.reachable ? r.travel_time_min.toFixed(1) : '—'}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {r.reachable ? (r.distance_m / 1000).toFixed(1) : '—'}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {r.reachable ? '✓' : '✗'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="bg-gray-50 px-2 py-1 text-[10px] text-sb-grey">
                  {matrixResult.meta.n_reachable}/{matrixResult.meta.n_destinations} reachable
                  · 1 hit
                </p>
              </div>
            )}

            {/* Route itinerary breakdown */}
            {analysisMode === 'route' && routeResult && (
              <div className="mt-4 rounded-md border border-gray-200">
                <div className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                  <p className="font-semibold text-sb-black">
                    {routeResult.summary.total_travel_time_min.toFixed(0)} min ·{' '}
                    {(routeResult.summary.total_distance_m / 1000).toFixed(1)} km
                  </p>
                  <p className="text-sb-grey">
                    {routeResult.summary.departure_time} → {routeResult.summary.arrival_time}
                  </p>
                </div>
                <ul className="divide-y divide-gray-100">
                  {routeResult.summary.legs.map((leg, i) => (
                    <li key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <span
                        className="inline-block h-2 w-2 shrink-0 rounded-full"
                        style={{
                          background: `rgb(${routeLegColor(leg.mode).slice(0, 3).join(',')})`,
                        }}
                      />
                      <span className="flex-1 font-semibold text-sb-black">{leg.mode}</span>
                      <span className="text-sb-grey">
                        {leg.travel_time_min.toFixed(0)} min ·{' '}
                        {(leg.distance_m / 1000).toFixed(1)} km
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showGtfsWindow && (
              <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <p className="font-semibold">PT schedule validity</p>
                <p>
                  Valid {meta!.gtfs_valid_from} → {meta!.gtfs_valid_to}
                </p>
              </div>
            )}

            {meta && (
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-sb-grey">
                <p>
                  <span className="font-semibold text-sb-black">Vendor:</span>{' '}
                  {meta.vendor}
                </p>
                <p>
                  <span className="font-semibold text-sb-black">Hits used:</span>{' '}
                  {meta.hits_used}
                  {analysisMode === 'h3' && (
                    <>
                      {' · '}
                      <span className="font-semibold text-sb-black">Cells:</span>{' '}
                      {(meta as H3Result['meta']).n_cells}
                    </>
                  )}
                  {analysisMode === 'isochrone' && (
                    <>
                      {' · '}
                      <span className="font-semibold text-sb-black">Shapes:</span>{' '}
                      {(meta as IsochroneResult['meta']).n_shapes}
                    </>
                  )}
                  {analysisMode === 'distance' && (
                    <>
                      {' · '}
                      <span className="font-semibold text-sb-black">Shapes:</span>{' '}
                      {(meta as DistanceMapResult['meta']).n_shapes}
                      {' · '}
                      {(meta as DistanceMapResult['meta']).travel_distance_km} km
                    </>
                  )}
                  {analysisMode === 'matrix' && (
                    <>
                      {' · '}
                      <span className="font-semibold text-sb-black">Reachable:</span>{' '}
                      {(meta as MatrixResult['meta']).n_reachable}/
                      {(meta as MatrixResult['meta']).n_destinations}
                    </>
                  )}
                </p>
              </div>
            )}

            <p className="mt-6 text-[10px] text-sb-grey">
              POST {SB_API_BASE}/oapi/processes/{PROCESS_ID[analysisMode]}/execution
            </p>
          </aside>
        </div>
      </main>
    </div>
  );
}
